import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useStore } from 'zustand';
import { Modal, Button } from 'antd';
import { ModalDragger } from './modal-dragger';
import DynamicRenderer from './DynamicRenderer';
import type { RenderNodesMap } from '@/types/commontypes';
import styles from '../../styles/global.module.css';
import { useChartShapes, useChartUIContext } from '../../contexts';
import { useGlobalShapeSelector } from '../../contexts/GlobalShapesContext';
import { useChartUIStoreSelector } from '../../stores/chartUIStore';
import { useShapePropertyEditor } from '../../hooks/useShapePropertyEditor';
import { globalShapeStore } from '../../stores/globalShapeStore';

interface ShapePropertyManagerProps {
  closePopup?: () => void;
}

const ShapePropertyManager: React.FC<ShapePropertyManagerProps> = ({ closePopup }) => {
  const { shapeStore } = useChartShapes();
  const renderNodes = useStore(shapeStore, s => s.renderNodes);
  const setRenderNodes = useStore(shapeStore, s => s.setRenderNodes);
  const { chartUIStore } = useChartUIContext();
  const setIsPopupVisible = useChartUIStoreSelector(chartUIStore, s => s.setIsPopupVisible);
  const selectedShape = useGlobalShapeSelector(s => s.selectedShape);

  const type = selectedShape?.type;

  const handleClosePopup = closePopup ?? (() => setIsPopupVisible(false));
  const handleClosePopupRef = useRef(handleClosePopup);
  handleClosePopupRef.current = handleClosePopup;

  const [isModalOpen, setIsModalOpen] = useState<boolean>(true);

  // Snapshot for Cancel/ESC revert (structuredClone 불가 — renderNodes에 d3 scale 함수 포함)
  const shapesSnapshotRef = useRef<RenderNodesMap>(JSON.parse(JSON.stringify(renderNodes)));
  const lastPropsSnapshotRef = useRef(globalShapeStore.getState().lastShapeProperties);

  // Shared editing logic
  const { currentProps, resolveProperties, handlePropChange } = useShapePropertyEditor();

  const resolvedProperties = resolveProperties(type);

  // ── Modal actions ──

  const handleModalClose = useCallback(() => {
    setIsModalOpen(false);
    handleClosePopupRef.current();
  }, []);

  const handleCancelClick = useCallback(() => {
    setRenderNodes(shapesSnapshotRef.current);
    globalShapeStore.getState().setLastShapeProperties(lastPropsSnapshotRef.current);
    handleModalClose();
  }, [setRenderNodes, handleModalClose]);

  // ESC handler
  const handleCancelClickRef = useRef(handleCancelClick);
  handleCancelClickRef.current = handleCancelClick;

  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleCancelClickRef.current();
      }
    };
    document.addEventListener('keydown', handleEscKey);
    return () => document.removeEventListener('keydown', handleEscKey);
  }, []);

  const handleOk = () => {
    setIsModalOpen(false);
    handleClosePopup();
  };

  const formatShapeTitle = (shape: string | undefined) => {
    if (shape) return shape.replace(/([A-Z])/g, ' $1').trim();
    return undefined;
  };

  return (
    <Modal
      title={formatShapeTitle(type)}
      open={isModalOpen}
      onCancel={handleModalClose}
      mask={{ closable: true }}
      modalRender={modal => <ModalDragger modal={modal} />}
      keyboard={true}
      className={styles.custom_modal}
      footer={[
        <Button key="cancel" onClick={handleCancelClick}>
          Cancel
        </Button>,
        <Button key="submit" type="primary" onClick={handleOk}>
          OK
        </Button>,
      ]}
    >
      {type && (
        <DynamicRenderer
          properties={resolvedProperties}
          shapeProps={currentProps ?? {}}
          onPropChange={handlePropChange}
          type={type}
        />
      )}
    </Modal>
  );
};

export default ShapePropertyManager;
